// ---------------------------------------------------------------------------
// Page Configuration Validation
// ---------------------------------------------------------------------------
// Validates page settings, tracking config, and generation inputs.

import { z } from "zod";
import { getBlockDefinition, normalizeBlockPropsForRender } from "../block-registry.js";
import { COMPONENT_TYPES } from "../constants.js";
import {
  CANONICAL_OUTPUT_KEYS,
  isPageTypeCompatibleWithOutput,
} from "../output-contract/index.js";
import { PAGE_TYPES, GENERATION_SOURCES, type PageType } from "../schema/index.js";
import { colorValueSchema } from "./puck-json.js";

// ---------------------------------------------------------------------------
// Generation input schemas
// ---------------------------------------------------------------------------

export const generatePageInputSchema = z.object({
  /** User's text prompt describing the desired page */
  prompt: z.string().min(10, "Prompt must be at least 10 characters").max(5000),
  /** Locale to use for native-language generation */
  locale: z.string().trim().min(2).max(35).regex(/^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/, "Invalid locale").optional(),
  /** Canonical output discriminator above the current pageType reality */
  outputKey: z.enum(CANONICAL_OUTPUT_KEYS).optional(),
  /** Optional page type hint for smart defaults */
  pageType: z.enum(PAGE_TYPES as unknown as [string, ...string[]]).optional(),
  /** Optional image URL for screenshot-to-page generation */
  imageUrl: z.string().url().optional(),
  /** Optional site scope so AI styling can resolve the canonical site theme */
  siteId: z.string().uuid().optional(),
  /** Optional structured business facts from GBP / onboarding to inject as a dedicated prompt layer */
  businessContext: z.string().min(1).max(8000).optional(),
  /** Whether to stream the response via SSE */
  stream: z.boolean().optional().default(true),
}).superRefine((value, ctx) => {
  if (value.outputKey && value.pageType) {
    const pageType = value.pageType as PageType;
    if (isPageTypeCompatibleWithOutput(value.outputKey, pageType)) {
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `pageType "${value.pageType}" is not compatible with output "${value.outputKey}"`,
      path: ["pageType"],
    });
  }
});

/** Input type (stream is optional, defaults to true after validation) */
export type GeneratePageInput = z.input<typeof generatePageInputSchema>;

export const refinePageInputSchema = z.object({
  /** ID of the page to refine (optional for conversational builder — new pages have no persisted ID) */
  pageId: z.string().uuid().optional(),
  /** Optional site scope so refinement can preserve the canonical site theme */
  siteId: z.string().uuid().optional(),
  /** Canonical output discriminator above the current pageType reality */
  outputKey: z.enum(CANONICAL_OUTPUT_KEYS).optional(),
  /** Optional page type hint to select the right instruction pack during refinement */
  pageType: z.enum(PAGE_TYPES as unknown as [string, ...string[]]).optional(),
  /** Refinement instructions */
  instructions: z.string().min(5).max(2000),
  /** Current Puck JSON to modify */
  currentPuckData: z.record(z.unknown()).refine(
    (data) => JSON.stringify(data).length <= 100_000,
    "Page data exceeds maximum size (100KB)",
  ),
}).superRefine((value, ctx) => {
  if (value.outputKey && value.pageType) {
    const pageType = value.pageType as PageType;
    if (isPageTypeCompatibleWithOutput(value.outputKey, pageType)) {
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `pageType "${value.pageType}" is not compatible with output "${value.outputKey}"`,
      path: ["pageType"],
    });
  }
});

export type RefinePageInput = z.infer<typeof refinePageInputSchema>;

export const generateSectionInputSchema = z.object({
  /** Component type to generate */
  componentType: z.enum(COMPONENT_TYPES as unknown as [string, ...string[]]),
  /** Context about what the section should contain */
  context: z.string().min(5).max(2000),
  /** Optional site scope so section generation can use the canonical site theme */
  siteId: z.string().uuid().optional(),
  /** Existing page context (other components) for consistency */
  pageContext: z.record(z.unknown()).optional(),
});

export type GenerateSectionInput = z.infer<typeof generateSectionInputSchema>;

// ---------------------------------------------------------------------------
// Page root props schema (Puck root.props — styling overrides for the page)
// Note: The DB-level PageSettings schema is in puck-json.ts (pageSettingsSchema).
// This schema validates root-level page styling props from the editor.
// ---------------------------------------------------------------------------

export const pageRootPropsSchema = z.object({
  /** Max content width */
  maxWidth: z.string().max(20).regex(/^\d+(px|rem|em|%|vw|vh)$|^(none|auto)$/, "Invalid CSS dimension").optional(),
  /** Body padding */
  padding: z.string().max(50).regex(/^[\d\s]+(px|rem|em|%|vw|vh)[\d\s]*(px|rem|em|%|vw|vh)?[\d\s]*(px|rem|em|%|vw|vh)?[\d\s]*(px|rem|em|%|vw|vh)?$|^(none|auto)$/, "Invalid CSS padding").optional(),
  /** Background color override */
  backgroundColor: colorValueSchema.optional(),
}).strict().optional();

export type PageRootProps = z.infer<typeof pageRootPropsSchema>;

// ---------------------------------------------------------------------------
// AI generation response validation
// ---------------------------------------------------------------------------

/**
 * Validates that AI-generated output looks like valid Puck JSON.
 * Lightweight check — full Puck validation happens via puck-json.ts schemas.
 */
export const aiGenerationResponseSchema = z.object({
  content: z.array(
    z.object({
      type: z.enum(COMPONENT_TYPES as unknown as [string, ...string[]]),
      props: z.record(z.unknown()).refine(
        (props) => typeof props.id === "string" && props.id.length > 0,
        { message: "Every component must have a non-empty 'id' prop" },
      ),
    }),
  ).min(1, "Generated page must have at least one component"),
  root: z.record(z.unknown()).optional(),
  zones: z.record(z.array(z.object({
    type: z.enum(COMPONENT_TYPES as unknown as [string, ...string[]]),
    props: z.record(z.unknown()).and(z.object({ id: z.string().min(1) }).passthrough()),
  }))).optional(),
});

export type AiGenerationResponse = z.infer<typeof aiGenerationResponseSchema>;

function isMissingRequiredValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim().length === 0;
  return false;
}

const RUNTIME_PROP_ALIASES: Partial<Record<(typeof COMPONENT_TYPES)[number], Record<string, string[]>>> = {
  "lead-form": {
    fieldList: ["fields"],
  },
  "cta-banner": {
    actionUrl: ["ctaUrl"],
  },
  "popup-trigger": {
    actionUrl: ["ctaUrl"],
  },
};

function validateRegistryComponent(
  component: { type: string; props: Record<string, unknown> },
  path: string,
): string[] {
  const definition = getBlockDefinition(component.type as (typeof COMPONENT_TYPES)[number]);
  if (!definition) {
    return [`${path} uses unsupported component type "${component.type}"`];
  }

  const issues: string[] = [];
  const normalizedProps = normalizeBlockPropsForRender(component.type, component.props);
  const aliases = RUNTIME_PROP_ALIASES[component.type as (typeof COMPONENT_TYPES)[number]] ?? {};
  const allowedProps = new Set([
    "id",
    ...definition.props.map((prop) => prop.name),
    ...Object.values(aliases).flat(),
  ]);

  for (const prop of definition.props) {
    const aliasKeys = aliases[prop.name] ?? [];
    const aliasSatisfied = aliasKeys.some(
      (key) => !isMissingRequiredValue(component.props[key]) || !isMissingRequiredValue(normalizedProps[key]),
    );
    if (
      prop.required
      && isMissingRequiredValue(component.props[prop.name])
      && isMissingRequiredValue(normalizedProps[prop.name])
      && !aliasSatisfied
    ) {
      issues.push(`${path}.${prop.name} is required for "${component.type}"`);
    }
  }

  for (const key of Object.keys(component.props)) {
    if (!allowedProps.has(key)) {
      issues.push(`${path}.${key} is not part of the canonical "${component.type}" contract`);
    }
  }

  return issues;
}

export function validateAiGenerationAgainstRegistry(
  response: AiGenerationResponse,
): string[] {
  const issues: string[] = [];

  response.content.forEach((component, index) => {
    issues.push(
      ...validateRegistryComponent(component, `content[${index}].props`),
    );
  });

  if (response.zones) {
    for (const [zoneKey, zoneItems] of Object.entries(response.zones)) {
      zoneItems.forEach((component, index) => {
        issues.push(
          ...validateRegistryComponent(
            component,
            `zones.${zoneKey}[${index}].props`,
          ),
        );
      });
    }
  }

  return issues;
}

// ---------------------------------------------------------------------------
// Generation source validation helper
// ---------------------------------------------------------------------------

export const generationSourceSchema = z.enum(
  GENERATION_SOURCES as unknown as [string, ...string[]],
);
